import { currentAccessToken, useAuthStore } from '../auth/store.js';

/**
 * The single HTTP boundary between the SPA and the INTEGRA API (§17.3).
 *
 * Components never call this directly — they use the TanStack Query hooks in
 * `src/api/hooks`, which §21.4 requires ("Never make API calls from components.
 * Use TanStack Query hooks in a dedicated hooks layer").
 */

// In dev the SPA (:5173) calls the API on :8080 directly. In a production build
// the default is empty, so requests go to `/api/v1/...` on the SPA's own origin
// and the host (Vercel) rewrites them to the API — same-site, which the
// SameSite=Strict refresh cookie requires (ADR-012). An explicit VITE_API_BASE
// still overrides either default.
const API_BASE =
  (import.meta.env['VITE_API_BASE'] as string | undefined) ??
  (import.meta.env.DEV ? 'http://localhost:8080' : '');

/** The API's error contract (§13.3). */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    correlationId: string;
  };
}

/**
 * An API failure carrying the server's own code and correlation id, so a
 * support conversation can start from the id rather than a screenshot.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly correlationId: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Skip the automatic refresh-and-retry (used by the refresh call itself). */
  noRetry?: boolean;
}

async function parseError(response: Response): Promise<ApiError> {
  let code = 'UNKNOWN';
  let message = `Request failed with status ${response.status}`;
  let correlationId = 'unknown';
  let details: Record<string, unknown> | undefined;
  try {
    const body = (await response.json()) as ApiErrorBody;
    code = body.error.code;
    message = body.error.message;
    correlationId = body.error.correlationId;
    details = body.error.details;
  } catch {
    // Non-JSON error (proxy, gateway): keep the status-derived message.
  }
  return new ApiError(response.status, code, message, correlationId, details);
}

/**
 * In-flight refresh, shared by every concurrent caller.
 *
 * This is NOT an optimisation — it is required for correctness. Refresh tokens
 * rotate on use and the server treats a replayed token as theft, revoking the
 * whole family (§14.1). Two simultaneous refreshes therefore destroy the
 * session: the first rotates, the second presents the superseded token and is
 * (correctly) rejected as reuse.
 *
 * Two things trigger exactly that: React StrictMode double-invoking the
 * bootstrap effect in development, and several queries 401-ing at once when an
 * access token expires. Both must collapse into ONE request.
 */
let inFlightRefresh: Promise<boolean> | null = null;

/** Exchange the httpOnly refresh cookie for a fresh access token. */
export function refreshSession(): Promise<boolean> {
  inFlightRefresh ??= performRefresh().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

async function performRefresh(): Promise<boolean> {
  const response = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: '{}',
    // Sends the httpOnly cookie; nothing here can read it.
    credentials: 'include',
  });
  if (!response.ok) return false;

  const body = (await response.json()) as {
    accessToken: string;
    userId: string;
    organisationId: string;
    platformRole?: 'none' | 'admin';
  };
  useAuthStore.getState().setSession({
    accessToken: body.accessToken,
    userId: body.userId,
    organisationId: body.organisationId,
    platformRole: body.platformRole ?? 'none',
  });
  return true;
}

/**
 * Perform an API request. On a 401 it attempts ONE silent refresh and replays
 * the request — an expired 15-minute access token should not surface as an
 * error the user has to act on.
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const token = currentAccessToken();
    return fetch(`${API_BASE}/api/v1${path}`, {
      method: options.method ?? 'GET',
      headers: {
        'content-type': 'application/json',
        ...(token !== null ? { authorization: `Bearer ${token}` } : {}),
      },
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      credentials: 'include',
    });
  };

  let response = await send();

  if (response.status === 401 && options.noRetry !== true) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await send();
    } else {
      useAuthStore.getState().clear();
    }
  }

  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Fetch binary media (a rendered image, §20.3) rather than JSON.
 *
 * The preview endpoint is workspace-authorised like every other route, so the
 * bytes cannot be reached by putting the URL in an `<img src>` — a browser
 * image request carries no Authorization header. The bytes are therefore pulled
 * here, with the same token and the same one-shot refresh-and-replay as `api`,
 * and the caller turns the Blob into an object URL.
 */
export async function apiBlob(path: string): Promise<Blob> {
  const send = async (): Promise<Response> => {
    const token = currentAccessToken();
    return fetch(`${API_BASE}/api/v1${path}`, {
      headers: { ...(token !== null ? { authorization: `Bearer ${token}` } : {}) },
      credentials: 'include',
    });
  };

  let response = await send();

  if (response.status === 401) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await send();
    } else {
      useAuthStore.getState().clear();
    }
  }

  if (!response.ok) throw await parseError(response);
  return await response.blob();
}

/**
 * End the session server-side: the API revokes the refresh-token family and
 * expires the httpOnly refresh cookie. A bare fetch (not `api`) because logout
 * must fire regardless of the access-token's state and needs no refresh-retry —
 * the in-memory store is cleared by the caller once this resolves.
 */
export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/api/v1/auth/logout`, { method: 'POST', credentials: 'include' });
}

export { API_BASE };
